import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Button, Spinner, Alert, Form, Modal, InputGroup } from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { AmazonScopeChip } from './AmazonScopeChip'
import '../styles/selectControl.css'

const EMPTY_FORM = { id: null, asin: null, target_acos: '', excluded: false, note: '', break_even_acos: null }
export const PRODUCT_EXCEPTION_SCOPE = 'A target ACoS or exclusion for one product, used by Anomaly Detection only; Inventory-Aware Bidding does not use these.'
export const TAKES_EFFECT_NOTE = 'Changes apply from the next run (scheduled or Run on the hub); existing alerts update then.'

// ACoS values are stored as fractions (0.35) and shown / edited as percents (35)
export const formatAcos = (fraction) => (fraction === null || fraction === undefined ? '' : `${(fraction * 100).toFixed(1)}%`)

const productLabel = (r) => [r.asin, r.amazon_sku, r.product_name].filter(Boolean).join(' · ')

const formFromRow = (row) => ({
    id: row.id,
    asin: { value: row.asin, label: productLabel(row) },
    target_acos: row.target_acos === null || row.target_acos === undefined ? '' : String(+(row.target_acos * 100).toFixed(2)),
    excluded: row.excluded,
    note: row.note || '',
    break_even_acos: row.break_even_acos,
})

/**
 * Add or edit one product exception.
 *
 * Pass ``row`` to edit a known exception (from the Product exceptions list), ``asin`` to open it
 * for a product (from an alert row: an existing exception for that ASIN is loaded and edited,
 * otherwise a new one is prepared), or neither to add with a product search.
 * ``onSaved`` is called after a save or removal.
 */
function AmazonProductExceptionModal({ show, accountId, row = null, asin = '', canManage, onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY_FORM)
    const [formError, setFormError] = useState('')
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const inputRefs = useRef({})
    const readOnly = !canManage

    const loadBreakEven = async (asinValue) => {
        try {
            const data = await amazonAPI.anomalySettings.searchProducts(accountId, asinValue, true)
            const match = data?.results?.[0]
            if (match) setForm(f => (f.asin?.value === asinValue ? { ...f, break_even_acos: match.break_even_acos } : f))
        } catch {
            // break-even is only shown for reference
        }
    }

    useEffect(() => {
        if (!show) return
        setFormError('')
        setConfirmDelete(false)
        if (row) {
            setForm(formFromRow(row))
            return
        }
        if (!asin) {
            setForm(EMPTY_FORM)
            return
        }
        // Opened for a product from an alert: edit its exception if it has one
        setForm({ ...EMPTY_FORM, asin: { value: asin, label: asin } })
        setLoading(true)
        amazonAPI.anomalySettings.getProducts({ account_id: accountId, search: asin, page_size: 50 })
            .then(data => {
                const existing = (data?.results || []).find(r => r.asin === asin)
                if (existing) setForm(formFromRow(existing))
                else loadBreakEven(asin)
            })
            .catch(err => setFormError(formatErrorResponse(err)))
            .finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, row, asin, accountId])

    const close = () => { if (!saving) onClose() }
    const updateForm = (patch) => { setForm(f => ({ ...f, ...patch })); setFormError('') }

    const loadAsinOptions = async (inputValue) => {
        if (!accountId || inputValue.trim().length < 2) return []
        try {
            const data = await amazonAPI.anomalySettings.searchProducts(accountId, inputValue.trim())
            return (data?.results || []).map(r => ({
                value: r.asin,
                label: productLabel(r) + (r.has_settings ? ' (already has an exception)' : ''),
                isDisabled: r.has_settings,
            }))
        } catch {
            return []
        }
    }

    const handleAsinChange = (opt) => {
        updateForm({ asin: opt, break_even_acos: null })
        if (opt) loadBreakEven(opt.value)
    }

    const focusField = (field) => {
        const el = inputRefs.current[field]
        if (el && typeof el.focus === 'function') el.focus()
    }

    const handleSave = async (e) => {
        e.preventDefault()
        setFormError('')
        if (!form.asin) {
            setFormError('Select a product'); focusField('asin'); return
        }
        const target = form.excluded || form.target_acos === '' ? null : Number(form.target_acos)
        if (target !== null && (Number.isNaN(target) || target <= 0 || target > 100)) {
            setFormError('Target ACoS: enter a percentage above 0 and up to 100'); focusField('target_acos'); return
        }
        if (target === null && !form.excluded) {
            setFormError('Enter a target ACoS or exclude the product'); focusField('target_acos'); return
        }
        const payload = { target_acos: target === null ? null : target / 100, excluded: form.excluded, note: form.note }
        setSaving(true)
        try {
            if (form.id) await amazonAPI.anomalySettings.updateProduct(form.id, payload)
            else await amazonAPI.anomalySettings.createProduct({ ...payload, account_id: accountId, asin: form.asin.value })
            onClose()
            onSaved?.()
        } catch (err) {
            setFormError(err.response?.data?.error || formatErrorResponse(err))
            if (err.response?.data?.field) focusField(err.response.data.field)
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        setSaving(true)
        try {
            await amazonAPI.anomalySettings.deleteProduct(form.id)
            onClose()
            onSaved?.()
        } catch (err) {
            setFormError(err.response?.data?.error || formatErrorResponse(err))
        } finally {
            setSaving(false)
        }
    }

    const fixedProduct = !!form.id || !!asin

    return (
        <Modal show={show} onHide={close} size="lg" backdrop="static" keyboard={false}>
            <Form onSubmit={handleSave}>
                <Modal.Header closeButton>
                    <Modal.Title>{form.id ? 'Product exception' : 'Add product exception'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <AmazonScopeChip scope="anomaly">Anomaly Detection · per product</AmazonScopeChip>
                        <div className="small text-body-secondary mt-1">{PRODUCT_EXCEPTION_SCOPE}</div>
                    </div>
                    {loading ? (
                        <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Label>Product</Form.Label>
                                    <AsyncSelect
                                        value={form.asin}
                                        onChange={handleAsinChange}
                                        loadOptions={loadAsinOptions}
                                        defaultOptions={false}
                                        cacheOptions
                                        isDisabled={fixedProduct || readOnly}
                                        ref={el => { inputRefs.current.asin = el }}
                                        placeholder="Search ASIN, SKU or name..."
                                        noOptionsMessage={({ inputValue }) => inputValue.trim().length < 2
                                            ? 'Type at least 2 characters to search...' : 'No matching products'}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Label>Target ACoS</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="number" min="0" max="100" step="any"
                                            value={form.target_acos}
                                            disabled={readOnly || form.excluded}
                                            placeholder={form.excluded ? 'Not used while excluded' : ''}
                                            ref={el => { inputRefs.current.target_acos = el }}
                                            onChange={e => updateForm({ target_acos: e.target.value })}
                                        />
                                        <InputGroup.Text>%</InputGroup.Text>
                                    </InputGroup>
                                    {form.asin && (
                                        <Form.Text>
                                            Used instead of break-even ACoS
                                            {form.break_even_acos === null || form.break_even_acos === undefined
                                                ? ' (break-even not available: missing price or COGS)'
                                                : ` (break-even ${formatAcos(form.break_even_acos)})`}.
                                        </Form.Text>
                                    )}
                                </Col>
                            </Row>
                            <Form.Check
                                type="switch"
                                id="product-exception-excluded"
                                className="mb-3"
                                label="Exclude this product from Anomaly Detection alerts"
                                checked={form.excluded}
                                disabled={readOnly}
                                onChange={e => updateForm({ excluded: e.target.checked, ...(e.target.checked ? { target_acos: '' } : {}) })}
                            />
                            <Form.Group>
                                <Form.Label>Note</Form.Label>
                                <Form.Control
                                    as="textarea" rows={2}
                                    value={form.note}
                                    disabled={readOnly}
                                    placeholder="Why this product has an exception, e.g. launch, pushed on purpose"
                                    onChange={e => updateForm({ note: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-block">
                    {formError && <Alert variant="danger" className="mb-2">{formError}</Alert>}
                    {canManage && <div className="small text-body-secondary mb-2">{TAKES_EFFECT_NOTE}</div>}
                    {confirmDelete ? (
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            <span className="me-auto">Remove the exception for this product?</span>
                            <Button variant="outline-primary" onClick={() => setConfirmDelete(false)} disabled={saving}>Cancel</Button>
                            <Button variant="danger" onClick={handleDelete} disabled={saving}>
                                {saving ? <Spinner size="sm" /> : 'Remove'}
                            </Button>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-end gap-2">
                            {canManage && form.id && (
                                <Button variant="outline-danger" className="me-auto" onClick={() => setConfirmDelete(true)} disabled={saving}>
                                    Remove
                                </Button>
                            )}
                            <Button variant="outline-primary" onClick={close} disabled={saving}>
                                {canManage ? 'Cancel' : 'Close'}
                            </Button>
                            {canManage && (
                                <Button type="submit" variant="primary" disabled={saving || loading}>
                                    {saving ? <Spinner size="sm" /> : 'Save'}
                                </Button>
                            )}
                        </div>
                    )}
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default AmazonProductExceptionModal
